let timerId = null;
let onVisibleHandler = null;
let onFocusHandler = null;
let onPageShowHandler = null;

// Ring buffer logging for analytics
function logWatchdogEvent(evt) {
    try {
        const key = 'qr_watchdog_log_v1';
        const raw = localStorage.getItem(key);
        let arr = Array.isArray(JSON.parse(raw || '[]')) ? JSON.parse(raw || '[]') : [];
        arr.push({ ts: Date.now(), ...evt });
        if (arr.length > 200) arr = arr.slice(arr.length - 200);
        localStorage.setItem(key, JSON.stringify(arr));
    } catch { /* swallow */ }
}

function removeVisibilityListeners() {
    if (onVisibleHandler) {
        document.removeEventListener('visibilitychange', onVisibleHandler);
        onVisibleHandler = null;
    }
    if (onFocusHandler) {
        window.removeEventListener('focus', onFocusHandler);
        onFocusHandler = null;
    }
    if (onPageShowHandler) {
        window.removeEventListener('pageshow', onPageShowHandler);
        onPageShowHandler = null;
    }
}

function installConsoleBuffer(max = 200) {
    if (window.__qrConsolePatched) return;
    window.__qrConsolePatched = true;
    const buf = [];
    const wrap = (level) => {
        const orig = console[level].bind(console);
        console[level] = (...args) => {
            try {
                buf.push({
                    ts: Date.now(),
                    level,
                    msg: args.map(a => {
                        try { return typeof a === 'string' ? a : JSON.stringify(a); }
                        catch { return String(a); }
                    }).join(' ')
                });
                if (buf.length > max) buf.shift();
            } catch {}
            orig(...args);
        };
    };
    ['log','info','warn','error'].forEach(wrap);
    window.__qrConsoleBuffer = buf;
}

export function startQrOpenWatchdog({
                                        paymentRequestId,
                                        fridgeId,
                                        amount,
                                        cartItems,
                                        user,
                                        // Don’t count while hidden; once visible give this much time to reach QR:
                                        visibleGraceMs = 15000,
                                        freshdeskSender, // async (details) => void
                                    }) {
    try {
        logWatchdogEvent({ event: 'start', paymentRequestId, fridgeId, amount });
        installConsoleBuffer();

        const device = {
            ua: navigator.userAgent,
            lang: navigator.language,
            platform: navigator.platform,
            viewport: { w: window.innerWidth, h: window.innerHeight },
            screen: { w: window.screen?.width, h: window.screen?.height, dpr: window.devicePixelRatio },
            connection: (navigator.connection && {
                effectiveType: navigator.connection.effectiveType,
                downlink: navigator.connection.downlink,
                rtt: navigator.connection.rtt,
                saveData: navigator.connection.saveData
            }) || undefined,
        };

        const journey = {
            paymentRequestId,
            fridgeId,
            amount,
            cartItems: (cartItems || []).map(x => ({ id: x.id, name: x.title, qty: x.quantity, price: x.price })),
            user: user ? { id: user.id, email: user.email } : { id: 'guest' },
            startedAt: Date.now(),
        };

        const state = {
            type: 'QR_OPEN_WATCHDOG',
            status: 'armed',
            device,
            journey,
            app: {
                armedVisibility: document.visibilityState,
                path: window.location.pathname,
                localStorage: {
                    selectedFridgeId: localStorage.getItem('selectedFridgeId'),
                    paymentStatus: localStorage.getItem('paymentStatus'),
                    paymentConfirmed: localStorage.getItem('paymentConfirmed'),
                    unlockCodeExpiry: localStorage.getItem('unlockCodeExpiry'),
                },
            },
            armedAt: Date.now(),
            // deadlineAt will be set when actually start the countdown (on visible)
            deadlineAt: null,
        };

        localStorage.setItem('qr_watchdog_v1', JSON.stringify(state));
        logWatchdogEvent({ event: 'armed', paymentRequestId, fridgeId });

        const fire = async () => {
            try {
                logWatchdogEvent({ event: 'fire_attempt', paymentRequestId });
                const armed = localStorage.getItem('qr_watchdog_v1');
                if (!armed) return;
                const data = JSON.parse(armed);
                if (data.status !== 'armed') return;

                const details = {
                    ...data,
                    firedAt: Date.now(),
                    app: {
                        ...data.app,
                        visibilityAtFire: document.visibilityState,
                    },
                    consoleTail: (window.__qrConsoleBuffer || []).slice(-200),
                };

                if (typeof freshdeskSender === 'function') {
                    await freshdeskSender(details);
                } else {
                    console.error('[QR Watchdog] Would send ticket:', details);
                }

                data.status = 'reported';
                data.reportedAt = Date.now();
                localStorage.setItem('qr_watchdog_v1', JSON.stringify(data));
                logWatchdogEvent({ event: 'reported', paymentRequestId });
            } catch (e) {
                console.error('[QR Watchdog] fire error', e);
                logWatchdogEvent({ event: 'fire_error', paymentRequestId, message: String(e?.message || e) });
            }
        };

        const startVisibleCountdown = () => {
            removeVisibilityListeners();
            if (timerId) clearTimeout(timerId);

            // update deadline from NOW (not from when we armed)
            const latest = JSON.parse(localStorage.getItem('qr_watchdog_v1') || '{}');
            latest.deadlineAt = Date.now() + visibleGraceMs;
            localStorage.setItem('qr_watchdog_v1', JSON.stringify(latest));
            logWatchdogEvent({ event: 'countdown_started', paymentRequestId, deadlineAt: latest.deadlineAt, graceMs: visibleGraceMs });

            timerId = window.setTimeout(fire, visibleGraceMs);
        };

        if (document.visibilityState === 'visible') {
            // already focused -> start grace now
            startVisibleCountdown();
        } else {
            // wait until visible/focus/pageshow (covers iOS Safari returning from Swish)
            onVisibleHandler = () => {
                if (document.visibilityState === 'visible') startVisibleCountdown();
            };
            onFocusHandler = () => startVisibleCountdown();
            onPageShowHandler = () => startVisibleCountdown();

            document.addEventListener('visibilitychange', onVisibleHandler);
            window.addEventListener('focus', onFocusHandler);
            window.addEventListener('pageshow', onPageShowHandler);
        }
    } catch (e) {
        console.error('[QR Watchdog] start error', e);
        logWatchdogEvent({ event: 'start_error', message: String(e?.message || e) });
    }
}

export function cancelQrOpenWatchdog(reason = 'qr_page_mounted') {
    try {
        if (timerId) clearTimeout(timerId);
        removeVisibilityListeners();

        const armed = localStorage.getItem('qr_watchdog_v1');
        if (!armed) { logWatchdogEvent({ event: 'cancel_no_state', reason }); return; }
        const data = JSON.parse(armed);
        if (data.status !== 'armed') { logWatchdogEvent({ event: 'cancel_ignored', reason, status: data.status }); return; }

        data.status = 'canceled';
        data.canceledAt = Date.now();
        data.cancelReason = reason;
        localStorage.setItem('qr_watchdog_v1', JSON.stringify(data));
        logWatchdogEvent({ event: 'canceled', reason, paymentRequestId: data?.journey?.paymentRequestId });
    } catch (e) {
        console.error('[QR Watchdog] cancel error', e);
        logWatchdogEvent({ event: 'cancel_error', reason, message: String(e?.message || e) });
    }
}

